import type { GameStateData } from '../game/GameState.js';
import { tileKey, type Tile } from '../game/types.js';
import { BONEYARD, BeliefTracker } from './BeliefTracker.js';

/**
 * Sample a concrete "world" consistent with the AI's information set: every
 * unseen tile gets assigned to either an opponent's hand or the boneyard,
 * respecting the known hand sizes / boneyard size.
 *
 * Used by SO-IS-MCTS — one determinization per iteration. The tree is shared
 * across iterations; only the underlying state changes between iterations.
 *
 * Sampling strategy: weighted Knuth-style allocation. Iterate every unseen
 * tile in a randomized order, draw a location with probability proportional
 * to the BeliefTracker posterior *and* remaining capacity at that location.
 * This satisfies the hand-size constraint by construction without rejection.
 *
 * Falls back to uniform-weighted sampling if every weight at the current
 * candidate is zero (e.g. after a hard zero from an [[observepass]] update
 * leaves no candidate seats for some tile).
 */
export interface Determinization {
  /** New hands array — viewSeat's hand is unchanged; opponents are sampled. */
  readonly hands: readonly (readonly Tile[])[];
  /** Sampled boneyard contents (order is randomized within the sample). */
  readonly boneyard: readonly Tile[];
}

export interface DeterminizerOptions {
  readonly rng?: () => number;
}

export class Determinizer {
  private readonly rng: () => number;

  constructor(opts: DeterminizerOptions = {}) {
    this.rng = opts.rng ?? Math.random;
  }

  sample(state: GameStateData, viewSeat: number, tracker: BeliefTracker): Determinization {
    const playerCount = state.playerCount;
    const ownHand = state.hands[viewSeat] ?? [];

    // Remaining capacities per location.
    const remaining: number[] = new Array<number>(playerCount).fill(0);
    for (let s = 0; s < playerCount; s += 1) {
      if (s === viewSeat) continue;
      remaining[s] = state.hands[s]?.length ?? 0;
    }
    let boneyardRemaining = state.boneyard.length;

    // Pre-compute per-tile legal location set (where posterior > 0). Sort
    // tiles ascending by |legal-set| — most-constrained-variable-first so a
    // tile that can only go to seat-2 isn't crowded out by random walk
    // putting unconstrained tiles there first. Tie-break randomly.
    const ordered = tracker.unseenTiles().map((tile) => {
      const legalLocs: number[] = [];
      for (let s = 0; s < playerCount; s += 1) {
        if (s === viewSeat) continue;
        if (tracker.pInLocation(tile, s) > 0) legalLocs.push(s);
      }
      if (tracker.pInLocation(tile, BONEYARD) > 0) legalLocs.push(BONEYARD);
      return { tile, legalCount: legalLocs.length, jitter: this.rng() };
    });
    ordered.sort((a, b) => a.legalCount - b.legalCount || a.jitter - b.jitter);

    const assignedHands: Tile[][] = Array.from({ length: playerCount }, () => []);
    assignedHands[viewSeat] = ownHand.slice();
    const assignedBoneyard: Tile[] = [];

    for (const { tile } of ordered) {
      const candidates: Array<{ loc: number; weight: number }> = [];
      for (let s = 0; s < playerCount; s += 1) {
        if (s === viewSeat) continue;
        const cap = remaining[s] ?? 0;
        if (cap <= 0) continue;
        const p = tracker.pInLocation(tile, s);
        if (p <= 0) continue;
        candidates.push({ loc: s, weight: p * cap });
      }
      if (boneyardRemaining > 0) {
        const p = tracker.pInLocation(tile, BONEYARD);
        if (p > 0) candidates.push({ loc: BONEYARD, weight: p * boneyardRemaining });
      }

      let chosen = pickWeighted(candidates, this.rng);
      if (chosen === null) {
        // Posterior-legal slots are all full. Fall back to uniform pick over
        // any remaining capacity — hand-size constraint outranks posterior
        // soft constraints. Hard zeros should be rare in practice once
        // sorted; if we still violate, the resulting world is best-effort.
        const fallback: Array<{ loc: number; weight: number }> = [];
        for (let s = 0; s < playerCount; s += 1) {
          if (s === viewSeat) continue;
          const cap = remaining[s] ?? 0;
          if (cap > 0) fallback.push({ loc: s, weight: cap });
        }
        if (boneyardRemaining > 0) {
          fallback.push({ loc: BONEYARD, weight: boneyardRemaining });
        }
        chosen = pickWeighted(fallback, this.rng);
      }
      if (chosen === null) continue;
      if (chosen === BONEYARD) {
        assignedBoneyard.push(tile);
        boneyardRemaining -= 1;
      } else {
        const target = assignedHands[chosen];
        if (target) target.push(tile);
        remaining[chosen] = (remaining[chosen] ?? 0) - 1;
      }
    }
    return { hands: assignedHands, boneyard: assignedBoneyard };
  }
}

function pickWeighted(
  candidates: ReadonlyArray<{ loc: number; weight: number }>,
  rng: () => number,
): number | null {
  let total = 0;
  for (const c of candidates) total += c.weight;
  if (total <= 0) return null;
  let r = rng() * total;
  for (const c of candidates) {
    r -= c.weight;
    if (r <= 0) return c.loc;
  }
  return candidates[candidates.length - 1]?.loc ?? null;
}

/**
 * Convenience: build a `GameStateData` whose hidden tiles match the sampled
 * determinization. Engine reducers consume this directly.
 */
export function applyDeterminization(
  state: GameStateData,
  det: Determinization,
): GameStateData {
  return { ...state, hands: det.hands, boneyard: det.boneyard };
}

export function tileSetSignature(hands: readonly (readonly Tile[])[]): string {
  const keys: string[] = [];
  for (const hand of hands) {
    for (const t of hand) keys.push(tileKey(t));
  }
  return keys.sort().join(',');
}
